import type { ComputedRef } from 'vue';
import { computed } from 'vue';

import i18n, { getTargetLocale, setLocale } from '@/i18n';
import type { AppLocale } from '@/i18n/messages';
import { APP_LOCALES } from '@/i18n/messages';

export type LocaleChoice = Readonly<{ value: AppLocale; label: string }>;

/** Language names are conventionally shown in their own language, so they are not translated copy. */
const LOCALE_LABELS: Readonly<Record<AppLocale, string>> = {
  en: 'English',
  ru: 'Русский',
};

export const LOCALE_CHOICES: ReadonlyArray<LocaleChoice> = APP_LOCALES.map((value) => ({
  value,
  label: LOCALE_LABELS[value],
}));

type UseLocaleSwitcher = Readonly<{
  locale: ComputedRef<AppLocale>;
  choices: ReadonlyArray<LocaleChoice>;
  switchLocale: (next: AppLocale) => Promise<void>;
}>;

export const useLocaleSwitcher = (): UseLocaleSwitcher => {
  /**
   * A read-only projection, not `i18n.global.locale` itself: that ref is writable, and assigning to
   * it would move the copy on screen while skipping `ensureLocaleMessages`, the document `lang` and
   * the stored choice. `switchLocale` is the only way in.
   */
  const locale = computed<AppLocale>(() => i18n.global.locale.value);

  /**
   * No reload. Every message read happens inside a `computed` — either directly, or through a
   * property getter on the few module-level catalogues (`ADMIN_BADGE`, `LOCK_REASON`/`LOCK_NOTICE`,
   * `PRICING_RULE_COLUMNS`) — so changing the locale re-renders the copy in place.
   * `i18n/__tests__/localeReactivity.spec.ts` fails if one of those getters becomes a value again,
   * which is the only way this could silently go back to needing a reload.
   */
  const switchLocale = async (next: AppLocale): Promise<void> => {
    // Against the locale being switched TO, not the one on screen: while a catalogue is in flight
    // the two differ, and comparing with the applied locale turns "go back to the language I had"
    // into a no-op that lets the request the user just cancelled arrive and win.
    if (next === getTargetLocale()) {
      return;
    }
    await setLocale(next);
  };

  return { locale, choices: LOCALE_CHOICES, switchLocale };
};
